/**
 * CRM Tracker v1.0 — deploy as a Shopify theme asset for CDN delivery
 *
 * Deploy:
 *   shopify theme push  (file lives at assets/tracker.js in your theme)
 *
 * Embed in theme.liquid (before </head>):
 *   <script>window._CRM_API = 'https://your-crm.railway.app';</script>
 *   <script src="{{ 'tracker.js' | asset_url }}" defer></script>
 *
 * Shopify serves assets/tracker.js via its global CDN automatically.
 * No request ever hits your CRM server for this file.
 */
(function (w, d) {
  'use strict';

  const API = w._CRM_API;
  if (!API) return;

  // ─── Utilities ─────────────────────────────────────────────────────────────

  const uid = () => 'sid_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

  const param = (name) => {
    const m = new RegExp('[?&]' + name + '=([^&]*)').exec(w.location.search);
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
  };

  const ls = {
    get:    (k)    => { try { return localStorage.getItem(k); } catch { return null; } },
    set:    (k, v) => { try { localStorage.setItem(k, v); }    catch {} },
    getJ:   (k)    => { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch { return null; } },
    setJ:   (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch {} },
  };

  // ─── Session ID (persists across pages in same browser) ────────────────────

  let sid = ls.get('_crm_sid');
  if (!sid) { sid = uid(); ls.set('_crm_sid', sid); }

  // ─── UTM capture (stored for session) ──────────────────────────────────────

  const utmSource   = param('utm_source');
  const utmMedium   = param('utm_medium');
  const utmCampaign = param('utm_campaign');
  const utmContent  = param('utm_content');
  const utmTerm     = param('utm_term');
  const fbclid      = param('fbclid');
  const gclid       = param('gclid');

  if (utmSource || fbclid || gclid) ls.setJ('_crm_utm', { utmSource, utmMedium, utmCampaign, utmContent, utmTerm, fbclid, gclid });
  const utm = ls.getJ('_crm_utm') || {};

  // Repeat-visit detection — count total visits in localStorage
  const visitCount = parseInt(ls.get('_crm_visit_count') || '0', 10) + 1;
  ls.set('_crm_visit_count', String(visitCount));
  const isRepeatVisitor = visitCount > 1;

  // ─── Send helper (beacon with fetch fallback) ───────────────────────────────

  const send = (path, data) => {
    const body = JSON.stringify({ sessionId: sid, ...data });
    const url  = API + path;
    // text/plain is CORS-safelisted: no preflight is ever sent, works on all browsers.
    // fetch fallback (keepalive) is used only when beacon is unavailable.
    if (w.navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain' }))) return;
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  };

  const event = (type, extra) => send('/api/v1/track/event', { eventType: type, url: w.location.href, page: w.location.pathname, ...extra });

  // ─── 1. Session start ───────────────────────────────────────────────────────

  send('/api/v1/track/session', {
    ...utm,
    referrer:        d.referrer || null,
    landingPage:     w.location.href,
    visitCount,
    isRepeatVisitor,
  });

  // ─── 2. Page view ───────────────────────────────────────────────────────────

  event('page_view', { title: d.title, referrer: d.referrer || null, ...utm });

  // ─── 3. Product view (Shopify sets ShopifyAnalytics.meta.product) ───────────

  const waitForShopify = (cb, tries) => {
    tries = tries || 0;
    if (w.ShopifyAnalytics?.meta?.product) return cb(w.ShopifyAnalytics.meta.product);
    if (tries < 10) setTimeout(() => waitForShopify(cb, tries + 1), 300);
  };

  waitForShopify((p) => {
    const variant = p.variants && p.variants[0];
    event('product_view', {
      productId:    String(p.id),
      productName:  p.title,
      productPrice: variant ? variant.price / 100 : null,
      metadata:     { type: p.type, vendor: p.vendor },
    });
  });

  // ─── 4. Collection view ─────────────────────────────────────────────────────

  if (w.ShopifyAnalytics?.meta?.page?.pageType === 'collection') {
    event('collection_view', {
      metadata: { collection: w.ShopifyAnalytics.meta.page.resourceType },
    });
  }

  // ─── 5. Add to cart (intercept fetch/XMLHttpRequest) ───────────────────────

  const origFetch = w.fetch;
  if (origFetch) {
    w.fetch = function (...args) {
      const [resource, init] = args;
      const isCartAdd = typeof resource === 'string' && resource.includes('/cart/add');
      const promise = origFetch.apply(w, args);

      if (isCartAdd) {
        promise.then((resp) => {
          resp.clone().json().then((data) => {
            event('add_to_cart', {
              cartValue:  (data.total_price || 0) / 100,
              quantity:   data.quantity,
              productId:  data.variant_id ? String(data.variant_id) : null,
              productName: data.title || null,
            });
          }).catch(() => {});
        }).catch(() => {});
      }
      return promise;
    };
  }

  // Intercept old-style XHR cart add
  const origXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && url.includes('/cart/add')) {
      this._isCartAdd = true;
    }
    return origXHROpen.apply(this, arguments);
  };

  const origXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body) {
    if (this._isCartAdd) {
      this.addEventListener('load', function () {
        try {
          const data = JSON.parse(this.responseText);
          event('add_to_cart', {
            cartValue:   (data.total_price || 0) / 100,
            quantity:    data.quantity,
            productId:   data.variant_id ? String(data.variant_id) : null,
            productName: data.title || null,
          });
        } catch {}
      });
    }
    return origXHRSend.apply(this, arguments);
  };

  // ─── 6. Checkout page tracking ─────────────────────────────────────────────

  const isCheckout = w.location.pathname.includes('/checkout') || w.location.pathname.includes('/checkouts');

  if (isCheckout) {
    event('checkout_started');

    // Track phone + email inputs
    const attachInput = (el) => {
      if (el._crmBound) return;
      el._crmBound = true;
      el.addEventListener('blur', function () {
        const v = this.value.trim();
        if (!v) return;
        const isPhone = this.type === 'tel' || /phone|mobile/i.test(this.name + (this.id || '') + (this.autocomplete || ''));
        const isEmail = this.type === 'email' || /email/i.test(this.name + (this.id || '') + (this.autocomplete || ''));
        if (isPhone)  event('phone_entered', { phone: v, metadata: { field: this.name } });
        if (isEmail)  event('email_entered', { email: v });
      });
    };

    const scanInputs = () => d.querySelectorAll('input[type="tel"],input[type="email"],input[name*="phone"],input[name*="email"],input[autocomplete*="tel"],input[autocomplete*="email"]').forEach(attachInput);

    scanInputs();
    new MutationObserver(scanInputs).observe(d.body, { childList: true, subtree: true });
  }

  // ─── 7. Scroll depth ────────────────────────────────────────────────────────

  const depths = new Set();
  const onScroll = () => {
    const pct = Math.round(((w.scrollY + w.innerHeight) / (d.documentElement.scrollHeight || 1)) * 100);
    [25, 50, 75, 100].forEach((d_) => {
      if (pct >= d_ && !depths.has(d_)) {
        depths.add(d_);
        event('scroll_depth', { metadata: { depth: d_ } });
      }
    });
  };
  w.addEventListener('scroll', onScroll, { passive: true });

  // ─── 8. Exit / session duration ─────────────────────────────────────────────

  const t0 = Date.now();
  const onExit = () => {
    event('exit', { metadata: { duration: Math.round((Date.now() - t0) / 1000) } });
  };
  d.addEventListener('visibilitychange', () => { if (d.visibilityState === 'hidden') onExit(); });
  w.addEventListener('pagehide', onExit);

}(window, document));
